import { PromiseExtended, Table } from 'dexie';
import { BaseModelForPush } from './base-model';
import { SyncRecord, SyncRecordStatus, SyncChanges } from '../types/sync-state';
import {
  IObservation,
  IObservationAnswer,
  IObservationQuestion,
  IObservationQuestionGroup,
  IObservationSection,
  IObservationTemplate,
  IQuestionOption,
  IVisitPlan,
  ISchoolVisit,
  ITeacherVisit,
  IRegion,
  ISchool,
  ITeacherContext,
  ISchoolClassContext,
  ISchoolClassSubjectContext,
  ILessonPlanMini,
  IBookChapterMini,
  ICoachObservation,
  IObservationFeedback,
  SectionType,
  QuestionPurpose,
  ObservationStatus,
  VisitStatus,
  VisitType,
  UserProfileType,
} from '../types/coaching';
import { IGrades, ISubject, IGradeSubject } from '../types/lessonplan';
import { v4 as uuidv4 } from 'uuid';

const SCHEMA_V7 = {
  observationTemplates: 'id',
  observationSections: 'id, template',
  observationQuestionGroups: 'id, section',
  observationQuestions: 'id, section, group, lessonPlan, purpose',
  questionOptions: 'id, question',
  observations:
    'id, template, coach, visit, userProfileType, userProfileId, syncStatus',
  observationAnswers: 'uuid, observation, question, studentNumber, syncStatus',
  observationFeedbacks:
    'id, userProfileId, userProfileType, syncStatus, observationDate',
  visitPlans: 'id, syncStatus',
  schoolVisits: 'id, school, visitPlan, syncStatus',
  teacherVisits:
    'id, schoolVisit, teacher, coach, status, userProfileType, userProfileId, syncStatus',
  coachesObservations: 'id, template, coach, visit, syncStatus',
  regions: 'id',
  schools: 'id, region',
  teachers: 'id, school',
  schoolClasses: 'id',
  schoolClassSubjects: 'id, schoolClass, gradeSubject, teacher',
  lessonPlansMini: 'id, gradeSubject',
  bookChapters: 'id, gradeSubject',
  grades: 'id',
  subjects: 'id',
  gradeSubjects: 'id, grade, subject',
};

const SCHEMA = {
  ...SCHEMA_V7,
  observationAnswers:
    'uuid, observation, question, studentNumber, studentName, syncStatus',
  observationPhotos: '++id, observationId, syncStatus',
};

const SCHEMA_VERSION = 9;

type ProfileFilter = {
  userProfileId: number;
  userProfileType?: UserProfileType | null;
};

type ProfileAwareRecord = {
  userProfileId?: number | null;
  userProfileType?: UserProfileType | null;
  coach?: number | null;
  profile?: number | null; // Backend sends 'profile' for observation feedbacks
};

const resolveProfileFields = (
  record: ProfileAwareRecord,
): {
  userProfileId: number | null;
  userProfileType: UserProfileType | null;
} => {
  const { userProfileId, userProfileType, coach, profile } = record;

  // Check userProfileId first, then profile (from backend), then coach as fallback
  const coachId = typeof coach === 'number' ? coach : null;
  const resolvedId = userProfileId ?? profile ?? coachId;

  let resolvedType: UserProfileType | null = userProfileType ?? null;

  if (resolvedType == null && typeof coach === 'number') {
    resolvedType = UserProfileType.Coach;
  }

  return { userProfileId: resolvedId, userProfileType: resolvedType };
};

const matchesProfileFilter = (
  record: ProfileAwareRecord,
  filter: ProfileFilter,
): boolean => {
  const { userProfileId, userProfileType } = resolveProfileFields(record);
  if (userProfileId == null || userProfileId !== filter.userProfileId)
    return false;

  const filterType = filter.userProfileType ?? null;
  if (filterType == null) return true;

  if (userProfileType != null) {
    return userProfileType === filterType;
  }

  if (
    filterType === UserProfileType.Coach &&
    typeof record.coach === 'number' &&
    record.coach === filter.userProfileId
  ) {
    return true;
  }

  return false;
};

export interface ObservationTemplateRecord
  extends IObservationTemplate,
    SyncRecord {}
export interface ObservationSectionRecord
  extends IObservationSection,
    SyncRecord {}
export interface ObservationQuestionGroupRecord
  extends IObservationQuestionGroup,
    SyncRecord {}
export interface ObservationQuestionRecord
  extends IObservationQuestion,
    SyncRecord {}
export interface QuestionOptionRecord extends IQuestionOption, SyncRecord {}
export interface ObservationRecord extends IObservation, SyncRecord {}
export interface ObservationAnswerRecord
  extends IObservationAnswer,
    SyncRecord {}
export interface ObservationFeedbackRecord
  extends IObservationFeedback,
    SyncRecord {}
export interface VisitPlanRecord extends IVisitPlan, SyncRecord {}
export interface SchoolVisitRecord extends ISchoolVisit, SyncRecord {}
export interface TeacherVisitRecord extends ITeacherVisit, SyncRecord {}
export interface RegionRecord extends IRegion, SyncRecord {}
export interface SchoolRecord extends ISchool, SyncRecord {}
export interface TeacherRecord extends ITeacherContext, SyncRecord {}
export interface CSchoolClassRecord extends ISchoolClassContext, SyncRecord {}
export interface CSchoolClassSubjectRecord
  extends ISchoolClassSubjectContext,
    SyncRecord {}
export interface LessonPlanMiniRecord extends ILessonPlanMini, SyncRecord {}
export interface BookChapterMiniRecord extends IBookChapterMini, SyncRecord {}
export interface CoachObservationRecord extends ICoachObservation, SyncRecord {}
export interface GradeRecord extends IGrades, SyncRecord {}
export interface SubjectRecord extends ISubject, SyncRecord {}
export interface CGradeSubjectRecord extends IGradeSubject, SyncRecord {}

export interface IObservationPhoto {
  id?: number;
  observationId: string;
  localUrl: string;
  blob?: Blob; // Actual data for Web persistence
  isBlurry: boolean;
  syncStatus: SyncRecordStatus;
  userProfileId?: number;
  userProfileType?: string;
}

export class CoachingDB extends BaseModelForPush {
  observationPhotos!: Table<IObservationPhoto>;
  observationTemplates!: Table<ObservationTemplateRecord>;
  observationSections!: Table<ObservationSectionRecord>;
  observationQuestionGroups!: Table<ObservationQuestionGroupRecord>;
  observationQuestions!: Table<ObservationQuestionRecord>;
  questionOptions!: Table<QuestionOptionRecord>;
  observations!: Table<ObservationRecord>;
  observationAnswers!: Table<ObservationAnswerRecord>;
  observationFeedbacks!: Table<ObservationFeedbackRecord>;
  visitPlans!: Table<VisitPlanRecord>;
  schoolVisits!: Table<SchoolVisitRecord>;
  teacherVisits!: Table<TeacherVisitRecord>;
  coachesObservations!: Table<CoachObservationRecord>;
  regions!: Table<RegionRecord>;
  schools!: Table<SchoolRecord>;
  teachers!: Table<TeacherRecord>;
  schoolClasses!: Table<CSchoolClassRecord>;
  schoolClassSubjects!: Table<CSchoolClassSubjectRecord>;
  lessonPlansMini!: Table<LessonPlanMiniRecord>;
  bookChapters!: Table<BookChapterMiniRecord>;
  grades!: Table<GradeRecord>;
  subjects!: Table<SubjectRecord>;
  gradeSubjects!: Table<CGradeSubjectRecord>;

  constructor() {
    super('coachingdb');
    this.version(7).stores(SCHEMA_V7);
    this.version(SCHEMA_VERSION).stores(SCHEMA);
    this.setupTableHooks(this.tables);
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  async getDefaultObservationTemplateId(): Promise<number> {
    const templates = await this.observationTemplates.toArray();

    // Edge case: templates not yet synced from server (fresh install / offline first launch).
    // Default to 2 (FICO V3) — the target template for all new observations post-migration.
    if (templates.length === 0) return 2;

    // Pick the highest template ID — new templates always have higher IDs,
    // so this naturally selects the latest active template without hardcoding.
    // Math.max with a floor of 2 ensures we never regress to FICO V1 (id=1)
    // even if the server temporarily returns only the old template.
    return Math.max(2, ...templates.map((t) => Number(t.id)));
  }

  async getAllObservations(): Promise<ObservationRecord[]> {
    return this.observations
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getAllBookChapters(): Promise<BookChapterMiniRecord[]> {
    return this.bookChapters
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async addObservation(data: IObservation): Promise<void> {
    const profileFields = resolveProfileFields(data);
    await this.observations.add({
      ...data,
      userProfileId: profileFields.userProfileId,
      userProfileType: profileFields.userProfileType,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    } as ObservationRecord);
  }

  async updateObservation(
    id: string,
    changes: Partial<IObservation>,
  ): Promise<number> {
    const existing = await this.observations.get(id);
    if (!existing) return 0;
    const profileFields = resolveProfileFields({
      userProfileId:
        changes.userProfileId !== undefined
          ? changes.userProfileId
          : existing.userProfileId,
      userProfileType:
        changes.userProfileType !== undefined
          ? changes.userProfileType
          : existing.userProfileType,
      coach: changes.coach !== undefined ? changes.coach : existing.coach,
    });

    const updateData: Partial<IObservation & { syncStatus: SyncRecordStatus }> =
      {
        ...changes,
        userProfileId: profileFields.userProfileId,
        userProfileType: profileFields.userProfileType,
      };
    return this.observations.update(id, updateData);
  }

  async saveObservationFeedback(id: string, feedback: string): Promise<number> {
    return this.updateObservation(id, { feedback });
  }

  async submitObservation(id: string): Promise<number> {
    // Only validate audio for teacher self-observations (not coach observations)
    const observation = await this.observations.get(id);

    if (observation?.userProfileType === UserProfileType.Teacher) {
      const audioStatus = await this.getObservationAudioStatus(id);

      if (!audioStatus.hasAudio) {
        throw new Error(
          'Cannot submit observation: No audio recording found. Please record audio before submitting.',
        );
      }
    }

    const now = new Date();

    return this.updateObservation(id, {
      status: ObservationStatus.Completed,
      observationDate: now.toISOString().split('T')[0],
    });
  }

  private async getNewCreatedObservations(): Promise<ObservationRecord[]> {
    const records = await this.getAllObservations();
    // Exclude teacher self-observations from being pushed (visit === null)
    const data = records.filter(
      (r) =>
        r.syncStatus === SyncRecordStatus.CreatedButNotSynced &&
        r.visit != null,
    );
    data.forEach((r) => (r.syncStatus = SyncRecordStatus.Created));
    return data;
  }

  private async getObservationsSyncData(): Promise<SyncRecord[]> {
    const records = await this.observations
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Deleted, SyncRecordStatus.Updated])
      .toArray();
    // Exclude teacher self-observations from sync stream (visit === null)
    return records.filter(
      (r) =>
        r.syncStatus !== SyncRecordStatus.CreatedButNotSynced &&
        (r as any).visit != null,
    );
  }

  private async getNewCreatedObservationAnswers(): Promise<
    ObservationAnswerRecord[]
  > {
    // MC20-18126: Push the syncStatus filter into Dexie so we never load every
    // answer for the user (10K+ rows × ~500 bytes ≈ 5–50 MB) just to discard
    // the synced ones. `syncStatus` is indexed on the observationAnswers table.
    const records = await this.observationAnswers
      .where('syncStatus')
      .equals(SyncRecordStatus.CreatedButNotSynced)
      .toArray();
    if (records.length === 0) return [];

    // Bulk-load only the observations referenced by these answers (deduped),
    // instead of doing one .get() per answer in a hot loop.
    const observationIds = Array.from(
      new Set(records.map((r) => r.observation)),
    );
    const observations = await this.observations
      .where('id')
      .anyOf(observationIds)
      .toArray();
    const visitByObsId = new Map<string, IObservation['visit'] | null>(
      observations.map((o) => [o.id, o.visit ?? null]),
    );

    const filtered: ObservationAnswerRecord[] = [];
    for (const r of records) {
      // Exclude answers belonging to self-observations (visit === null)
      if (visitByObsId.get(r.observation) == null) continue;
      r.syncStatus = SyncRecordStatus.Created;
      filtered.push({ ...r });
    }
    return filtered;
  }

  private async getObservationAnswersSyncData(): Promise<SyncRecord[]> {
    const records = await this.observationAnswers
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Deleted, SyncRecordStatus.Updated])
      .toArray();
    if (records.length === 0) return [];

    // MC20-18126: Bulk-load referenced observations once instead of doing one
    // .get() per answer (N+1 → 1 query). Cuts a 10K-answer push from ~10K
    // round-trips to two.
    const observationIds = Array.from(
      new Set(records.map((r) => (r as any).observation as string)),
    );
    const observations = await this.observations
      .where('id')
      .anyOf(observationIds)
      .toArray();
    const visitByObsId = new Map<string, IObservation['visit'] | null>(
      observations.map((o) => [o.id, o.visit ?? null]),
    );

    // Only include answers for observations that have a non-null visit
    const filtered: SyncRecord[] = [];
    for (const r of records) {
      if (r.syncStatus === SyncRecordStatus.CreatedButNotSynced) continue;
      if (visitByObsId.get((r as any).observation) == null) continue;
      filtered.push({ ...(r as any) });
    }
    return filtered;
  }

  private async getRecordsToBeSynced(
    model: Table,
  ): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await model
      .where('syncStatus')
      .anyOf([
        SyncRecordStatus.CreatedButNotSynced,
        SyncRecordStatus.Updated,
        SyncRecordStatus.Deleted,
      ])
      .toArray();
    return records.map((data) => ({
      ...data,
      syncStatus:
        data.syncStatus === SyncRecordStatus.CreatedButNotSynced
          ? SyncRecordStatus.Created
          : data.syncStatus,
    }));
  }

  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const observations = [
      ...(await this.getObservationsSyncData()),
      ...(await this.getNewCreatedObservations()),
      ...(await this.getRecordsToBeSynced(this.coachesObservations)),
    ];
    const observationAnswers = [
      ...(await this.getObservationAnswersSyncData()),
      ...(await this.getNewCreatedObservationAnswers()),
    ].map((record) => {
      const { studentName: _studentName, ...rest } = record as any;
      return rest;
    });

    const observationFeedbacks = [
      ...(await this.getObservationFeedbacksSyncData()),
      ...(await this.getNewCreatedObservationFeedbacks()),
    ];

    const records: Record<string, SyncRecord[]> = {
      observation: observations,
      observationAnswer: observationAnswers,
      observationFeedback: observationFeedbacks,
      visitPlan: await this.getRecordsToBeSynced(this.visitPlans),
      schoolVisit: await this.getRecordsToBeSynced(this.schoolVisits),
      teacherVisit: await this.getRecordsToBeSynced(this.teacherVisits),
    };

    return records;
  }

  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;
    const modelMap: Record<string, Table> = {
      visitPlan: this.visitPlans,
      schoolVisit: this.schoolVisits,
      teacherVisit: this.teacherVisits,
      observation: this.observations,
      coachesObservations: this.coachesObservations,
      observationAnswer: this.observationAnswers,
      observationFeedback: this.observationFeedbacks,
    };

    for (const [model, table] of Object.entries(modelMap)) {
      const modelChanges = syncChanges[model];
      if (!modelChanges) continue;

      // Handle all types of changes: created, updated, and deleted
      for (const changeType of ['created', 'updated', 'deleted']) {
        const records = modelChanges[changeType as keyof typeof modelChanges];
        if (records && Array.isArray(records) && records.length > 0) {
          const primaryKey = table.schema.primKey.name;
          const ids = records.map((r: any) => r[primaryKey]);
          updates += await this._updateSyncData(table, ids, status);
        }
      }
    }

    return updates;
  }

  async getObservationContext() {
    const [
      regions,
      schools,
      teachers,
      schoolClassSubjects,
      gradeSubjects,
      lessonPlans,
    ] = await Promise.all([
      this.regions
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.schools
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.teachers
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.schoolClassSubjects
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.gradeSubjects
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.lessonPlansMini
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
    ]);

    return {
      regions,
      schools,
      teachers,
      schoolClassSubjects,
      gradeSubjects,
      lessonPlans,
    };
  }

  async getBaseObservationContext() {
    const [
      regions,
      schools,
      teachers,
      gradeSubjects,
      grades,
      subjects,
      schoolClasses,
    ] = await Promise.all([
      this.regions
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.schools
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.teachers
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.gradeSubjects
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.grades
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.subjects
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
      this.schoolClasses
        .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
        .toArray(),
    ]);

    const gradeSubjectsWithLabels = gradeSubjects.map((gs) => {
      const grade = grades.find((g) => g.id === gs.grade);
      const subject = subjects.find((s) => s.id === gs.subject);

      return {
        ...gs,
        gradeLabel: grade?.label || `Grade ${gs.grade}`,
        subjectLabel: subject?.label || `Subject ${gs.subject}`,
      };
    });

    return {
      regions,
      schools,
      teachers,
      gradeSubjects: gradeSubjectsWithLabels,
      schoolClasses,
    };
  }

  async getTeacherClassSubjects(teacherId: number) {
    return this.schoolClassSubjects
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.teacher === teacherId,
      )
      .toArray();
  }

  async getTeachersBySchool(schoolId: number) {
    return this.teachers
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.school === schoolId,
      )
      .toArray();
  }

  async getLessonPlansForClassSubject(classSubjectId: number) {
    const classSubject = await this.schoolClassSubjects.get(classSubjectId);
    if (!classSubject) return [];
    return this.lessonPlansMini
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.gradeSubject === classSubject.gradeSubject,
      )
      .toArray();
  }

  async getLessonPlansForGradeSubject(gradeSubjectId: number) {
    return this.lessonPlansMini
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.gradeSubject === gradeSubjectId,
      )
      .toArray();
  }

  async getSchoolVisitsByVisitPlan(visitPlanId: string) {
    return this.schoolVisits.where('visitPlan').equals(visitPlanId).toArray();
  }

  async getObservationById(id: string): Promise<ObservationRecord | undefined> {
    return this.observations.where('id').equals(id).first();
  }

  async getObservationByVisit(
    visitId: string,
    filter?: {
      userProfileId?: number | null;
      userProfileType?: UserProfileType | null;
    },
  ): Promise<ObservationRecord | undefined> {
    const visitObservations = await this.observations
      .where('visit')
      .equals(visitId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();

    if (visitObservations.length === 0) return undefined;
    if (!filter || filter.userProfileId == null) {
      return visitObservations[0];
    }

    const profileFilter: ProfileFilter = {
      userProfileId: filter.userProfileId,
      userProfileType: filter.userProfileType ?? null,
    };

    const matched = visitObservations.find((record) =>
      matchesProfileFilter(record, profileFilter),
    );

    return matched ?? visitObservations[0];
  }

  async getObservationsForProfile({
    userProfileId,
    userProfileType = null,
  }: {
    userProfileId: number;
    userProfileType?: UserProfileType | null;
  }): Promise<ObservationRecord[]> {
    if (userProfileId == null || Number.isNaN(userProfileId)) return [];

    const filter: ProfileFilter = {
      userProfileId,
      userProfileType,
    };

    const result = new Map<string, ObservationRecord>();
    const collect = (records: ObservationRecord[]) => {
      records.forEach((record) => {
        if (record.syncStatus === SyncRecordStatus.Deleted) return;
        if (!matchesProfileFilter(record, filter)) return;
        if (!result.has(record.id)) {
          result.set(record.id, record);
        }
      });
    };

    const profileRecords = await this.observations
      .where('userProfileId')
      .equals(userProfileId)
      .toArray();
    collect(profileRecords);

    if (userProfileType == null || userProfileType === UserProfileType.Coach) {
      const legacyRecords = await this.observations
        .where('coach')
        .equals(userProfileId)
        .toArray();
      collect(legacyRecords);
    }

    return Array.from(result.values());
  }

  async getClassSubjectById(
    id: number,
  ): Promise<CSchoolClassSubjectRecord | undefined> {
    return this.schoolClassSubjects.where('id').equals(id).first();
  }

  /**
   * `schoolClassSubject` may be a real school-class-subject id or (legacy) a
   * timetable row id. Returns the id to use with `schoolClassSubjects`.
   */
  private async resolveEffectiveSchoolClassSubjectId(
    observation: ObservationRecord,
  ): Promise<number | null> {
    const raw = observation.schoolClassSubject;
    if (raw == null) return null;

    const direct = await this.getClassSubjectById(raw);
    if (direct) return raw;

    try {
      const { lessonPlanDB } = await import('..');
      const timetable = await lessonPlanDB.schoolClassTimetables.get(raw);
      if (timetable?.schoolClassSubject != null) {
        return timetable.schoolClassSubject;
      }
    } catch {
      // lesson plan DB may be unavailable in some contexts
    }
    return null;
  }

  /**
   * Subject id for filtering manual observation questions (`IObservationQuestion.subject`)
   * after API → Dexie sync. Uses class subject → grade subject → subject when available;
   * resolves timetable id stored as `schoolClassSubject` when needed; falls back to
   * linked `teacherVisits.gradeSubject` when class subject is missing (e.g. schedule v2).
   */
  async resolveVisitSubjectForObservation(
    observation: ObservationRecord | undefined,
  ): Promise<number | null | undefined> {
    if (observation === undefined) return undefined;

    const effectiveId =
      await this.resolveEffectiveSchoolClassSubjectId(observation);
    if (effectiveId != null) {
      const classSubject = await this.getClassSubjectById(effectiveId);
      if (classSubject?.gradeSubject != null) {
        const gs = await this.gradeSubjects.get(classSubject.gradeSubject);
        if (gs?.subject != null) return gs.subject;
      }
    }

    if (observation.visit) {
      const tv = await this.teacherVisits.get(observation.visit);
      if (tv?.gradeSubject != null) {
        const gs = await this.gradeSubjects.get(tv.gradeSubject);
        if (gs?.subject != null) return gs.subject;
      }
    }

    return null;
  }

  async getObservationSectionsByTemplateId(
    templateId: number,
  ): Promise<ObservationSectionRecord[]> {
    return this.observationSections
      .where('template')
      .equals(templateId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('order');
  }

  async getObservationQuestionGroupsBySectionId(
    sectionId: number,
  ): Promise<ObservationQuestionGroupRecord[]> {
    return this.observationQuestionGroups
      .where('section')
      .equals(sectionId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('order');
  }

  async getObservationQuestionsBySectionId(
    sectionId: number,
  ): Promise<ObservationQuestionRecord[]> {
    return this.observationQuestions
      .where('section')
      .equals(sectionId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('order');
  }

  async getQuestionOptionsByQuestionIds(
    questionIds: number[],
  ): Promise<QuestionOptionRecord[]> {
    if (questionIds.length === 0) return [];
    return this.questionOptions
      .where('question')
      .anyOf(questionIds)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('order');
  }

  async getObservationAnswersByObservationId(
    observationId: string,
    studentNumber?: number | null,
  ): Promise<ObservationAnswerRecord[]> {
    let collection = this.observationAnswers
      .where('observation')
      .equals(observationId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted);

    if (studentNumber !== undefined) {
      collection = collection.filter((r) => r.studentNumber === studentNumber);
    }

    return collection.toArray();
  }

  async getLessonPlanQuestionsByPurpose(
    lessonPlanId: number | null,
    purpose: QuestionPurpose,
  ): Promise<ObservationQuestionRecord[]> {
    if (lessonPlanId == null) return [];
    return this.observationQuestions
      .filter(
        (q) =>
          q.lessonPlan === lessonPlanId &&
          q.purpose === purpose &&
          q.syncStatus !== SyncRecordStatus.Deleted,
      )
      .sortBy('order');
  }

  async getObservationQuestionsBySectionIds(
    sectionIds: number[],
  ): Promise<ObservationQuestionRecord[]> {
    if (sectionIds.length === 0) return [];
    return this.observationQuestions
      .where('section')
      .anyOf(sectionIds)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('order');
  }

  async getObservationSummaryData(observationId: string): Promise<{
    questions: ObservationQuestionRecord[];
    options: QuestionOptionRecord[];
    answers: ObservationAnswerRecord[];
  }> {
    const observation = await this.getObservationById(observationId);
    if (!observation) {
      return { questions: [], options: [], answers: [] };
    }

    const sections = await this.getObservationSectionsByTemplateId(
      observation.template,
    );

    const regularSectionIds = sections
      .filter((s) => s.sectionType === SectionType.Regular)
      .map((s) => s.id);

    const questions =
      await this.getObservationQuestionsBySectionIds(regularSectionIds);

    if (sections.some((s) => s.sectionType === SectionType.Fidelity)) {
      const fidelityQuestions = await this.getLessonPlanQuestionsByPurpose(
        observation.lessonPlan,
        QuestionPurpose.Fidelity,
      );
      questions.push(...fidelityQuestions);
    }

    const questionIds = questions.map((q) => q.id);
    const [options, answers] = await Promise.all([
      this.getQuestionOptionsByQuestionIds(questionIds),
      this.getObservationAnswersByObservationId(observationId),
    ]);

    return { questions, options, answers };
  }

  async getObservationScoreData(observationId: string): Promise<{
    sections: ObservationSectionRecord[];
    questions: ObservationQuestionRecord[];
    options: QuestionOptionRecord[];
    answers: ObservationAnswerRecord[];
  }> {
    const observation = await this.getObservationById(observationId);
    if (!observation) {
      return { sections: [], questions: [], options: [], answers: [] };
    }

    const sections = await this.getObservationSectionsByTemplateId(
      observation.template,
    );
    const sectionIds = sections.map((s) => s.id);
    const questions =
      await this.getObservationQuestionsBySectionIds(sectionIds);

    if (sections.some((s) => s.sectionType === SectionType.Fidelity)) {
      const fidelityQuestions = await this.getLessonPlanQuestionsByPurpose(
        observation.lessonPlan,
        QuestionPurpose.Fidelity,
      );
      questions.push(...fidelityQuestions);
    }

    const questionIds = questions.map((q) => q.id);

    const [options, answers] = await Promise.all([
      this.getQuestionOptionsByQuestionIds(questionIds),
      this.getObservationAnswersByObservationId(observationId),
    ]);

    return { sections, questions, options, answers };
  }

  async getObservationCompletionData(observationId: string): Promise<{
    observation: ObservationRecord | undefined;
    sections: ObservationSectionRecord[];
    questions: ObservationQuestionRecord[];
    answers: ObservationAnswerRecord[];
  }> {
    const observation = await this.getObservationById(observationId);
    if (!observation) {
      return {
        observation: undefined,
        sections: [],
        questions: [],
        answers: [],
      };
    }

    const sections = await this.getObservationSectionsByTemplateId(
      observation.template,
    );
    const sectionIds = sections.map((s) => s.id);
    const questions =
      await this.getObservationQuestionsBySectionIds(sectionIds);

    // add fidelity questions if there are fidelity sections
    if (sections.some((s) => s.sectionType === SectionType.Fidelity)) {
      const fidelityQuestions = await this.getLessonPlanQuestionsByPurpose(
        observation.lessonPlan,
        QuestionPurpose.Fidelity,
      );
      questions.push(...fidelityQuestions);
    }

    const answers =
      await this.getObservationAnswersByObservationId(observationId);

    return { observation, sections, questions, answers };
  }

  async saveObservationAnswer(
    observationId: string,
    questionId: number,
    optionId: number,
    studentNumber: number | null = null,
    lpFollowed?: boolean | null,
  ): Promise<void> {
    // Use a transaction to prevent race conditions during rapid clicks
    await this.transaction('rw', this.observationAnswers, async () => {
      const existing = await this.observationAnswers
        .where('question')
        .equals(questionId)
        .filter(
          (r) =>
            r.observation === observationId &&
            r.studentNumber === studentNumber,
        )
        .first();

      if (existing) {
        const updateData: Partial<ObservationAnswerRecord> = {
          singleChoiceOption: optionId,
          syncStatus: SyncRecordStatus.Updated,
          ...(lpFollowed !== undefined && { isLpFollowed: lpFollowed }),
        };
        await this.observationAnswers.update(existing.uuid, updateData);
      } else {
        await this.observationAnswers.add({
          uuid: uuidv4(),
          observation: observationId,
          question: questionId,
          studentNumber,
          studentName: null,
          answerText: null,
          singleChoiceOption: optionId,
          multiChoiceOptions: [],
          wrongChoiceOptions: [],
          syncStatus: SyncRecordStatus.CreatedButNotSynced,
          isLpFollowed: lpFollowed !== undefined ? lpFollowed : null,
        } as ObservationAnswerRecord);
      }
    });
  }

  async saveMultiChoiceAnswer(
    observationId: string,
    questionId: number,
    optionId: number,
    studentName: string | null,
    totalOptions: number,
  ): Promise<void> {
    await this.transaction('rw', this.observationAnswers, async () => {
      const existing = await this.observationAnswers
        .where('observation')
        .equals(observationId)
        .filter(
          (r) => r.question === questionId && r.studentName === studentName,
        )
        .first();

      // 3-state cycle: unselected → correct → wrong → unselected
      const currentCorrect = existing?.multiChoiceOptions ?? [];
      const currentWrong = existing?.wrongChoiceOptions ?? [];

      const isCorrect = currentCorrect.includes(optionId);
      const isWrong = currentWrong.includes(optionId);

      let updatedCorrect: number[];
      let updatedWrong: number[];

      if (!isCorrect && !isWrong) {
        // unselected → correct
        updatedCorrect = [...new Set([...currentCorrect, optionId])];
        updatedWrong = currentWrong;
      } else if (isCorrect) {
        // correct → wrong
        updatedCorrect = currentCorrect.filter((id) => id !== optionId);
        updatedWrong = [...new Set([...currentWrong, optionId])];
      } else {
        // wrong → unselected
        updatedCorrect = currentCorrect;
        updatedWrong = currentWrong.filter((id) => id !== optionId);
      }

      const studentScores = {
        studentName: studentName ?? null,
        score: updatedCorrect.length,
        totalScore: totalOptions,
        correctOptions: updatedCorrect,
        wrongOptions: updatedWrong,
      };

      if (existing) {
        await this.observationAnswers.update(existing.uuid, {
          multiChoiceOptions: updatedCorrect,
          wrongChoiceOptions: updatedWrong,
          studentScores,
          ...(existing.syncStatus !== SyncRecordStatus.CreatedButNotSynced && {
            syncStatus: SyncRecordStatus.Updated,
          }),
        } as Partial<ObservationAnswerRecord>);
      } else if (updatedCorrect.length > 0 || updatedWrong.length > 0) {
        await this.observationAnswers.add({
          uuid: uuidv4(),
          observation: observationId,
          question: questionId,
          studentNumber: null,
          studentName,
          answerText: null,
          singleChoiceOption: null,
          multiChoiceOptions: updatedCorrect,
          wrongChoiceOptions: updatedWrong,
          studentScores,
          syncStatus: SyncRecordStatus.CreatedButNotSynced,
          isLpFollowed: null,
        } as ObservationAnswerRecord);
      }
    });
  }

  async saveLpFollowed(
    observationId: string,
    questionId: number,
    studentNumber: number | null,
    lpFollowed: boolean,
  ): Promise<void> {
    const existing = await this.observationAnswers
      .where('question')
      .equals(questionId)
      .filter(
        (r) =>
          r.observation === observationId && r.studentNumber === studentNumber,
      )
      .first();
    if (existing) {
      await this.observationAnswers.update(existing.uuid, {
        isLpFollowed: lpFollowed,
        ...(existing.syncStatus !== SyncRecordStatus.CreatedButNotSynced && {
          syncStatus: SyncRecordStatus.Updated,
        }),
      });
    }
  }

  async getTeacherNameByObservationId(
    observationId: string,
  ): Promise<string | null> {
    const observation = await this.observations
      .where('id')
      .equals(observationId)
      .first();

    if (!observation) return null;

    // if schoolClassSubject is null (Schedule V2 style observation), attempt to
    // resolve the teacher name via the linked teacherVisit (observation.visit)
    if (observation.schoolClassSubject == null) {
      if (observation.visit) {
        const tv = await this.teacherVisits
          .where('id')
          .equals(observation.visit)
          .first();
        if (tv && tv.teacher != null) {
          const teacher = await this.teachers
            .where('id')
            .equals(tv.teacher)
            .first();
          return teacher?.teacherName || null;
        }
      }
      return null;
    }

    const classSubject = await this.schoolClassSubjects
      .where('id')
      .equals(observation.schoolClassSubject as number)
      .first();

    if (!classSubject) return null;

    const teacher = await this.teachers
      .where('id')
      .equals(classSubject.teacher)
      .first();

    return teacher?.teacherName || null;
  }

  async createVisitPlan(data: {
    fromDate: string;
    toDate: string;
    name?: string;
  }): Promise<string> {
    // Check if ANY active visit plan already exists (since we only allow one at a time)
    const existingPlan = await this.visitPlans
      .where('syncStatus')
      .anyOf([
        SyncRecordStatus.Created,
        SyncRecordStatus.CreatedButNotSynced,
        SyncRecordStatus.Updated,
      ])
      .first();

    if (existingPlan) {
      // Update existing plan with new dates and return its ID
      await this.updateVisitPlan(existingPlan.id, {
        fromDate: data.fromDate,
        toDate: data.toDate,
      });
      return existingPlan.id;
    }

    // Create new plan
    const planId = uuidv4();
    await this.visitPlans.add({
      id: planId,
      fromDate: data.fromDate,
      toDate: data.toDate,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    });
    return planId;
  }

  async updateVisitPlan(
    id: string,
    changes: Partial<{ fromDate: string; toDate: string; name?: string }>,
  ): Promise<number> {
    const existing = await this.visitPlans.get(id);
    if (!existing) return 0;

    const updateData: Partial<{
      fromDate?: string;
      toDate?: string;
      name?: string;
      syncStatus?: SyncRecordStatus;
    }> = {
      ...changes,
    };

    return this.visitPlans.update(id, updateData);
  }

  async updateSchoolVisit(
    schoolVisitId: string,
    changes: {
      coachId?: number;
      status?: VisitStatus;
      comments?: string;
    },
  ): Promise<number> {
    const existing = await this.schoolVisits.get(schoolVisitId);
    if (!existing) return 0;

    const updateData = {
      ...changes,
    };

    return this.schoolVisits.update(schoolVisitId, updateData);
  }

  // Add a new teacher visit with correct sync status
  async addTeacherVisit(data: ITeacherVisit): Promise<void> {
    const profileFields = resolveProfileFields(data);
    await this.teacherVisits.add({
      ...data,
      userProfileId: profileFields.userProfileId,
      userProfileType: profileFields.userProfileType,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    } as TeacherVisitRecord);
  }

  async getAllRegions(): Promise<RegionRecord[]> {
    return this.regions
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getAllSchools(): Promise<SchoolRecord[]> {
    return this.schools
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getAllGrades(): Promise<GradeRecord[]> {
    return this.grades
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getSubjectsForGrade(gradeId: number): Promise<SubjectRecord[]> {
    const gradeSubjectLinks = await this.gradeSubjects
      .filter(
        (gs) =>
          gs.syncStatus !== SyncRecordStatus.Deleted && gs.grade === gradeId,
      )
      .toArray();
    if (gradeSubjectLinks.length === 0) return [];
    const subjectIds = gradeSubjectLinks.map((gs) => gs.subject);
    return this.subjects
      .where('id')
      .anyOf(subjectIds)
      .filter((s) => s.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getSpecificSchool(schoolId: number): Promise<SchoolRecord | undefined> {
    return this.schools.where('id').equals(Number(schoolId)).first();
  }

  async getGradeSubjectId(
    gradeId: number,
    subjectId: number,
  ): Promise<number | null> {
    const gradeSubject = await this.gradeSubjects
      .where({
        grade: gradeId,
        subject: subjectId,
      })
      .first();
    return gradeSubject?.id ?? null;
  }

  // Coach Observations CRUD methods
  async getAllCoachObservations(): Promise<CoachObservationRecord[]> {
    return this.coachesObservations
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getCoachObservationById(
    id: string,
  ): Promise<CoachObservationRecord | undefined> {
    return this.coachesObservations.where('id').equals(id).first();
  }

  async getCoachObservationsByCoach(
    coachId: number,
  ): Promise<CoachObservationRecord[]> {
    return this.coachesObservations
      .where('coach')
      .equals(coachId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getCoachObservationsByVisit(
    visitId: string,
  ): Promise<CoachObservationRecord[]> {
    return this.coachesObservations
      .where('visit')
      .equals(visitId)
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  // Update an existing teacher visit and set sync status if needed
  async updateTeacherVisit(
    id: string,
    changes: Partial<ITeacherVisit>,
  ): Promise<number> {
    const existing = await this.teacherVisits.get(id);
    if (!existing) return 0;
    const profileFields = resolveProfileFields({
      userProfileId:
        changes.userProfileId !== undefined
          ? changes.userProfileId
          : existing.userProfileId,
      userProfileType:
        changes.userProfileType !== undefined
          ? changes.userProfileType
          : existing.userProfileType,
      coach: changes.coach !== undefined ? changes.coach : existing.coach,
    });
    const updateData: Partial<ITeacherVisit> = {
      ...changes,
      userProfileId: profileFields.userProfileId,
      userProfileType: profileFields.userProfileType,
    };

    return this.teacherVisits.update(id, updateData);
  }

  async getTeacherVisitsForProfile({
    userProfileId,
    userProfileType = null,
  }: {
    userProfileId: number;
    userProfileType?: UserProfileType | null;
  }): Promise<TeacherVisitRecord[]> {
    if (userProfileId == null || Number.isNaN(userProfileId)) return [];

    const filter: ProfileFilter = {
      userProfileId,
      userProfileType,
    };

    const result = new Map<string, TeacherVisitRecord>();

    const collect = (records: TeacherVisitRecord[]) => {
      records.forEach((record) => {
        if (record.syncStatus === SyncRecordStatus.Deleted) return;
        if (!matchesProfileFilter(record, filter)) return;
        if (!result.has(record.id)) {
          result.set(record.id, record);
        }
      });
    };

    const profileRecords = await this.teacherVisits
      .where('userProfileId')
      .equals(userProfileId)
      .toArray();
    collect(profileRecords);

    if (userProfileType == null || userProfileType === UserProfileType.Coach) {
      const legacyRecords = await this.teacherVisits
        .where('coach')
        .equals(userProfileId)
        .toArray();
      collect(legacyRecords);
    }

    return Array.from(result.values());
  }

  getVisitPlan(visitPlanId: string) {
    return this.visitPlans.get(visitPlanId);
  }

  async findSchoolVisit(
    schoolId: number,
    visitPlanId: string,
  ): Promise<SchoolVisitRecord | undefined> {
    return this.schoolVisits
      .filter(
        (sv) =>
          sv.school === schoolId &&
          sv.visitPlan === visitPlanId &&
          sv.syncStatus !== SyncRecordStatus.Deleted,
      )
      .first();
  }

  // Renamed method to better reflect its purpose
  async createSchoolVisitWithCoaches(data: {
    schoolId: number;
    visitPlanId: string;
    coachIds: number[];
  }): Promise<string> {
    const schoolVisitId = uuidv4();
    await this.schoolVisits.add({
      id: schoolVisitId,
      school: data.schoolId,
      visitPlan: data.visitPlanId,
      coaches: data.coachIds,
      scheduledDate: null,
      visitDate: null,
      comments: '',
      status: VisitStatus.Incomplete,
      type: VisitType.Planned,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    });
    return schoolVisitId;
  }

  // Keep for backwards compatibility but update to use array
  async createSchoolVisitWithCoach(data: {
    schoolId: number;
    visitPlanId: string;
    coachId: number;
  }): Promise<string> {
    return this.createSchoolVisitWithCoaches({
      schoolId: data.schoolId,
      visitPlanId: data.visitPlanId,
      coachIds: [data.coachId],
    });
  }

  async assignOrUpdateSchoolVisitCoach(data: {
    schoolId: number;
    visitPlanId: string;
    coachId: number;
  }): Promise<string> {
    const existingSchoolVisit = await this.findSchoolVisit(
      data.schoolId,
      data.visitPlanId,
    );

    if (existingSchoolVisit) {
      await this.updateSchoolVisitCoach(existingSchoolVisit.id, data.coachId);
      return existingSchoolVisit.id;
    } else {
      return this.createSchoolVisitWithCoach(data);
    }
  }

  async updateSchoolVisitCoach(
    schoolVisitId: string,
    coachId: number,
  ): Promise<number> {
    const existing = await this.schoolVisits.get(schoolVisitId);
    if (!existing) {
      throw new Error(`School visit with ID ${schoolVisitId} not found`);
    }

    const currentCoaches = existing.coaches || [];
    // Add the coach if not already in the array
    const updatedCoaches = currentCoaches.includes(coachId)
      ? currentCoaches
      : [...currentCoaches, coachId];

    const updateData: Partial<SchoolVisitRecord> = {
      coaches: updatedCoaches,
    };

    return this.schoolVisits.update(schoolVisitId, updateData);
  }

  // New methods for multiple coach assignment
  async assignMultipleCoachesToSchool(data: {
    schoolId: number;
    visitPlanId: string;
    coachIds: number[];
  }): Promise<string[]> {
    const { schoolId, visitPlanId, coachIds } = data;

    // Get existing school visit for this school and visit plan (should be only one)
    const existingVisits = await this.getSchoolVisitsBySchoolAndPlan(
      schoolId,
      visitPlanId,
    );

    if (existingVisits.length > 0) {
      // Update the existing school visit with the new coaches array
      const existingVisit = existingVisits[0];
      await this.schoolVisits.update(existingVisit.id, {
        coaches: coachIds,
      });

      // Mark any additional duplicate visits for deletion (cleanup old data)
      for (let i = 1; i < existingVisits.length; i++) {
        await this.schoolVisits.update(existingVisits[i].id, {
          syncStatus: SyncRecordStatus.Deleted,
        });
      }

      return [existingVisit.id];
    } else {
      // Create new school visit with all coaches in the array
      const schoolVisitId = uuidv4();
      await this.schoolVisits.add({
        id: schoolVisitId,
        school: schoolId,
        visitPlan: visitPlanId,
        coaches: coachIds,
        scheduledDate: null,
        visitDate: null,
        comments: '',
        status: VisitStatus.Incomplete,
        type: VisitType.Planned,
        syncStatus: SyncRecordStatus.CreatedButNotSynced,
      });
      return [schoolVisitId];
    }
  }

  // Helper method to safely remove school visits (used for complete replacement scenarios)
  private async removeAllSchoolVisitsForPlan(
    schoolId: number,
    visitPlanId: string,
  ): Promise<void> {
    const existingVisits = await this.schoolVisits
      .filter(
        (sv) =>
          sv.school === schoolId &&
          sv.visitPlan === visitPlanId &&
          sv.syncStatus !== SyncRecordStatus.Deleted,
      )
      .toArray();

    // Mark all existing visits as deleted
    for (const visit of existingVisits) {
      await this.schoolVisits.update(visit.id, {
        syncStatus: SyncRecordStatus.Deleted,
      });
    }
  }

  async getSchoolVisitsBySchoolAndPlan(
    schoolId: number,
    visitPlanId: string,
  ): Promise<SchoolVisitRecord[]> {
    return this.schoolVisits
      .filter(
        (sv) =>
          sv.school === schoolId &&
          sv.visitPlan === visitPlanId &&
          sv.syncStatus !== SyncRecordStatus.Deleted,
      )
      .toArray();
  }

  async getAssignedCoachesForSchool(
    schoolId: number,
    visitPlanId: string,
  ): Promise<number[]> {
    const schoolVisits = await this.getSchoolVisitsBySchoolAndPlan(
      schoolId,
      visitPlanId,
    );
    // With new structure, we should have at most one school visit per school
    return schoolVisits.length > 0 ? schoolVisits[0].coaches || [] : [];
  }

  // Method to completely replace all coaches for a school (if needed)
  async replaceAllCoachesForSchool(data: {
    schoolId: number;
    visitPlanId: string;
    coachIds: number[];
  }): Promise<string[]> {
    const { schoolId, visitPlanId, coachIds } = data;
    const schoolVisitIds: string[] = [];

    // Remove all existing school visits for this school and visit plan
    await this.removeAllSchoolVisitsForPlan(schoolId, visitPlanId);

    // Create a new school visit for each coach
    for (const coachId of coachIds) {
      const schoolVisitId = await this.createSchoolVisitWithCoach({
        schoolId,
        visitPlanId,
        coachId,
      });
      schoolVisitIds.push(schoolVisitId);
    }

    return schoolVisitIds;
  }

  // RM Analytics Methods
  async getCoachesObservationAnalytics(): Promise<
    Array<{
      id: number;
      name: string;
      observationsConducted: number;
      observationsRemaining: number;
      progressPercentage: number;
    }>
  > {
    // Get active visit plan first for edge case handling
    const activeVisitPlan = await this.getActiveVisitPlan();

    // Get all observations grouped by coach
    const allObservations = await this.coachesObservations
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();

    const coachObservationsMap = new Map<number, CoachObservationRecord[]>();

    // Group observations by coach
    allObservations.forEach((obs) => {
      const coachId = obs.coach;
      if (coachId == null) return;
      if (!coachObservationsMap.has(coachId)) {
        coachObservationsMap.set(coachId, []);
      }
      const arr = coachObservationsMap.get(coachId);
      if (arr) arr.push(obs);
    });

    // Edge Case 1: Include coaches with 0 observations if there's an active visit plan
    if (activeVisitPlan) {
      const schoolVisits = await this.schoolVisits
        .filter(
          (sv) =>
            sv.visitPlan === activeVisitPlan.id &&
            sv.syncStatus !== SyncRecordStatus.Deleted,
        )
        .toArray();

      const allCoachIds = [
        ...new Set(schoolVisits.flatMap((sv) => sv.coaches)),
      ];

      // Initialize coaches with 0 observations
      allCoachIds.forEach((coachId) => {
        if (!coachObservationsMap.has(coachId)) {
          coachObservationsMap.set(coachId, []);
        }
      });
    }

    const coachAnalytics = [];

    for (const [coachId, observations] of coachObservationsMap) {
      const observationsConducted = observations.filter(
        (obs) => obs.status === ObservationStatus.Completed,
      ).length;

      const observationsRemaining = observations.filter(
        (obs) => obs.status === ObservationStatus.InProgress,
      ).length;

      const total = observations.length;
      const progressPercentage =
        total > 0 ? Math.round((observationsConducted / total) * 100) : 0;

      coachAnalytics.push({
        id: coachId,
        name: `Coach ${coachId}`, // This will be resolved in the component using coach profiles
        observationsConducted,
        observationsRemaining,
        progressPercentage,
      });
    }

    return coachAnalytics;
  }

  // RM-specific method to get analytics for coaches under this RM only
  async getCoachesObservationAnalyticsForRM(rmCoachIds: number[]): Promise<
    Array<{
      id: number;
      name: string;
      observationsConducted: number;
      observationsRemaining: number;
      progressPercentage: number;
    }>
  > {
    if (rmCoachIds.length === 0) return [];

    // Get all observations from coaches under this RM
    const allObservations = await this.coachesObservations
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.coach !== null &&
          rmCoachIds.includes(record.coach),
      )
      .toArray();

    const coachObservationsMap = new Map<number, CoachObservationRecord[]>();

    // Group observations by coach (only RM's coaches)
    allObservations.forEach((obs) => {
      const coachId = obs.coach;
      if (coachId == null) return;
      if (!coachObservationsMap.has(coachId)) {
        coachObservationsMap.set(coachId, []);
      }
      const arr = coachObservationsMap.get(coachId);
      if (arr) arr.push(obs);
    });

    // Initialize all RM's coaches with 0 observations if they don't have any
    rmCoachIds.forEach((coachId) => {
      if (!coachObservationsMap.has(coachId)) {
        coachObservationsMap.set(coachId, []);
      }
    });

    const coachAnalytics = [];

    for (const [coachId, observations] of coachObservationsMap) {
      const observationsConducted = observations.filter(
        (obs) => obs.status === ObservationStatus.Completed,
      ).length;

      const observationsRemaining = observations.filter(
        (obs) => obs.status === ObservationStatus.InProgress,
      ).length;

      const total = observations.length;
      const progressPercentage =
        total > 0 ? Math.round((observationsConducted / total) * 100) : 0;

      coachAnalytics.push({
        id: coachId,
        name: `Coach ${coachId}`, // This will be resolved in the component using coach profiles
        observationsConducted,
        observationsRemaining,
        progressPercentage,
      });
    }

    return coachAnalytics;
  }

  // RM-specific method to get analytics based on teacher visits and observations
  async getCoachAnalyticsBasedOnTeacherVisits(): Promise<
    Array<{
      id: number;
      name: string;
      totalPlannedVisits: number;
      observationsConducted: number;
      observationsRemaining: number;
      progressPercentage: number;
    }>
  > {
    // Get active visit plan
    const activeVisitPlan = await this.getActiveVisitPlan();
    if (!activeVisitPlan) return [];

    // Get all school visits in the active visit plan
    const schoolVisits = await this.schoolVisits
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.visitPlan === activeVisitPlan.id,
      )
      .toArray();

    if (schoolVisits.length === 0) return [];

    // Extract unique coach IDs from all school visits in active plan
    const coachIds = [
      ...new Set(schoolVisits.flatMap((sv) => sv.coaches || [])),
    ];

    if (coachIds.length === 0) return [];

    // Get all teacher visits scheduled by coaches in active plan (only those with scheduled dates)
    const allTeacherVisits = await this.teacherVisits
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.scheduledDate !== null && // Only count planned visits
          record.coach !== null &&
          coachIds.includes(record.coach),
      )
      .toArray();

    // Get all observations from coaches in active plan
    const allObservations = await this.coachesObservations
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.coach !== null &&
          coachIds.includes(record.coach),
      )
      .toArray();

    const coachTeacherVisitsMap = new Map<number, TeacherVisitRecord[]>();
    const coachObservationsMap = new Map<number, CoachObservationRecord[]>();

    // Group teacher visits by coach (only those with scheduled dates - implemented in the query that fetches allTeacherVisits)
    allTeacherVisits.forEach((visit) => {
      const coachId = visit.coach;
      if (coachId == null) return;
      if (!coachTeacherVisitsMap.has(coachId)) {
        coachTeacherVisitsMap.set(coachId, []);
      }
      const tvArr = coachTeacherVisitsMap.get(coachId);
      if (tvArr) tvArr.push(visit);
    });

    // Group observations by coach
    allObservations.forEach((obs) => {
      const coachId = obs.coach;
      if (coachId == null) return;
      if (!coachObservationsMap.has(coachId)) {
        coachObservationsMap.set(coachId, []);
      }
      const cobArr = coachObservationsMap.get(coachId);
      if (cobArr) cobArr.push(obs);
    });

    // Initialize all coaches with empty arrays if they don't have any data
    coachIds.forEach((coachId: number) => {
      if (!coachTeacherVisitsMap.has(coachId)) {
        coachTeacherVisitsMap.set(coachId, []);
      }
      if (!coachObservationsMap.has(coachId)) {
        coachObservationsMap.set(coachId, []);
      }
    });

    const coachAnalytics = [];

    for (const coachId of coachIds) {
      const teacherVisits = coachTeacherVisitsMap.get(coachId) || [];
      const observations = coachObservationsMap.get(coachId) || [];

      const totalPlannedVisits = teacherVisits.length;

      // Create a set of teacher visit IDs for faster lookup
      const teacherVisitIds = new Set(teacherVisits.map((tv) => tv.id));

      // Count completed observations that are linked to scheduled teacher visits only
      const observationsConducted = observations.filter(
        (obs) =>
          obs.status === ObservationStatus.Completed &&
          obs.visit && // Must have a visit ID
          teacherVisitIds.has(obs.visit), // Must be linked to a scheduled teacher visit
      ).length;

      // Observations remaining = planned visits that don't have completed observations yet
      const observationsRemaining = Math.max(
        0,
        totalPlannedVisits - observationsConducted,
      );

      const progressPercentage =
        totalPlannedVisits > 0
          ? Math.round((observationsConducted / totalPlannedVisits) * 100)
          : 0;

      coachAnalytics.push({
        id: coachId,
        name: `Coach ${coachId}`, // This will be resolved in the component using coach profiles
        totalPlannedVisits,
        observationsConducted,
        observationsRemaining,
        progressPercentage,
      });
    }

    return coachAnalytics;
  }

  async getActiveVisitPlan(): Promise<VisitPlanRecord | undefined> {
    const plans = await this.visitPlans
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();

    if (plans.length === 0) return undefined;
    if (plans.length === 1) return plans[0];

    // Edge Case 2: Smart visit plan selection for multiple plans
    const currentDate = new Date().toISOString().split('T')[0];

    const activePlan = plans.find((plan) => {
      return plan.fromDate <= currentDate && plan.toDate >= currentDate;
    });

    // If no plan contains current date, return the most recent one
    return (
      activePlan ||
      plans.sort(
        (a, b) =>
          new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime(),
      )[0]
    );
  }

  async getSchoolCompletionStats(): Promise<{
    totalSchools: number;
    completedSchools: number;
    completionPercentage: number;
  }> {
    const allObservations = await this.coachesObservations
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();

    const totalObservations = allObservations.length;
    const completedObservations = allObservations.filter(
      (obs) => obs.status === ObservationStatus.Completed,
    ).length;

    const completionPercentage =
      totalObservations > 0
        ? Math.round((completedObservations / totalObservations) * 100)
        : 0;

    return {
      totalSchools: totalObservations,
      completedSchools: completedObservations,
      completionPercentage,
    };
  }

  // RM-specific method to get completion stats for coaches under this RM only
  async getSchoolCompletionStatsForRM(rmCoachIds: number[]): Promise<{
    totalSchools: number;
    completedSchools: number;
    completionPercentage: number;
  }> {
    if (rmCoachIds.length === 0) {
      return {
        totalSchools: 0,
        completedSchools: 0,
        completionPercentage: 0,
      };
    }

    const allObservations = await this.coachesObservations
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.coach !== null &&
          rmCoachIds.includes(record.coach),
      )
      .toArray();

    const totalObservations = allObservations.length;
    const completedObservations = allObservations.filter(
      (obs) => obs.status === ObservationStatus.Completed,
    ).length;

    const completionPercentage =
      totalObservations > 0
        ? Math.round((completedObservations / totalObservations) * 100)
        : 0;

    return {
      totalSchools: totalObservations,
      completedSchools: completedObservations,
      completionPercentage,
    };
  }

  // RM-specific method to get school completion stats based on teacher visits
  async getSchoolCompletionStatsBasedOnTeacherVisits(): Promise<{
    completionPercentage: number;
    totalSchools: number;
    completedSchools: number;
  }> {
    // Get active visit plan
    const activeVisitPlan = await this.getActiveVisitPlan();
    if (!activeVisitPlan)
      return { completionPercentage: 0, totalSchools: 0, completedSchools: 0 };

    // Get all school visits in the active visit plan
    const schoolVisits = await this.schoolVisits
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.visitPlan === activeVisitPlan.id,
      )
      .toArray();

    if (schoolVisits.length === 0)
      return { completionPercentage: 0, totalSchools: 0, completedSchools: 0 };

    // Extract unique coach IDs from all school visits in active plan
    const coachIds = [
      ...new Set(schoolVisits.flatMap((sv) => sv.coaches || [])),
    ];

    if (coachIds.length === 0)
      return { completionPercentage: 0, totalSchools: 0, completedSchools: 0 };

    // Get all teacher visits (planned visits) by coaches in active plan
    const allTeacherVisits = await this.teacherVisits
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.scheduledDate !== null && // Only count planned visits
          record.coach !== null &&
          coachIds.includes(record.coach),
      )
      .toArray();

    // Get all observations by coaches in active plan
    const allObservations = await this.coachesObservations
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.coach !== null &&
          coachIds.includes(record.coach),
      )
      .toArray();

    // Only count completed observations that are linked to teacher visits
    const teacherVisitIds = allTeacherVisits.map((tv) => tv.id);
    const completedObservations = allObservations.filter(
      (obs) =>
        obs.status === ObservationStatus.Completed &&
        obs.visit && // Must have a visit ID
        teacherVisitIds.includes(obs.visit), // Must be linked to a teacher visit
    );

    const totalPlannedVisits = allTeacherVisits.length;
    const totalCompletedObservations = completedObservations.length;

    // Completion percentage = (completed observations / total planned visits) * 100
    const completionPercentage =
      totalPlannedVisits > 0
        ? Math.round((totalCompletedObservations / totalPlannedVisits) * 100)
        : 0;

    return {
      completionPercentage,
      totalSchools: schoolVisits.length, // Total schools in active plan
      completedSchools: totalCompletedObservations, // This could represent completed observations instead
    };
  }

  // Check if a coach has started or completed observations for a specific school
  async hasCoachStartedObservationsForSchool(
    coachId: number,
    schoolId: number,
    visitPlanId: string,
  ): Promise<boolean> {
    // Get the school visit for this coach, school, and visit plan
    const schoolVisit = await this.schoolVisits
      .filter(
        (sv) =>
          sv.school === schoolId &&
          sv.visitPlan === visitPlanId &&
          sv.coaches?.includes(coachId) &&
          sv.syncStatus !== SyncRecordStatus.Deleted,
      )
      .first();

    if (!schoolVisit) return false;

    // Get teacher visits for this school visit and coach
    const teacherVisits = await this.teacherVisits
      .filter(
        (tv) =>
          tv.schoolVisit === schoolVisit.id &&
          tv.coach === coachId &&
          tv.syncStatus !== SyncRecordStatus.Deleted,
      )
      .toArray();

    if (teacherVisits.length === 0) return false;

    // Get all observations for this coach that are in progress or completed
    const allCoachObservations = await this.coachesObservations
      .where('coach')
      .equals(coachId)
      .filter(
        (obs) =>
          obs.syncStatus !== SyncRecordStatus.Deleted &&
          (obs.status === ObservationStatus.InProgress ||
            obs.status === ObservationStatus.Completed),
      )
      .toArray();

    // Check if any of these observations are linked to our teacher visits
    const teacherVisitIds = teacherVisits.map((tv) => tv.id);
    const matchingObservations = allCoachObservations.filter(
      (obs) => obs.visit && teacherVisitIds.includes(obs.visit),
    );

    return matchingObservations.length > 0;
  }

  // Observation Feedbacks methods
  async addObservationFeedback(data: IObservationFeedback): Promise<void> {
    await this.observationFeedbacks.add({
      ...data,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    } as ObservationFeedbackRecord);
  }

  async getObservationFeedbacksForProfile({
    userProfileId,
  }: {
    userProfileId: number;
  }): Promise<ObservationFeedbackRecord[]> {
    // Backend already filters feedbacks for the logged-in user,
    // so we return all non-deleted feedbacks from IndexedDB
    if (userProfileId == null || Number.isNaN(userProfileId)) return [];

    return this.observationFeedbacks
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  private async getObservationFeedbacksSyncData(): Promise<SyncRecord[]> {
    const records = await this.observationFeedbacks
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Deleted, SyncRecordStatus.Updated])
      .toArray();
    return (
      records
        .filter((r) => r.syncStatus !== SyncRecordStatus.CreatedButNotSynced)
        // Safely strip the local-only draft field before pushing across the network
        .map(({ draftTeacherResponse, ...rest }) => rest)
    );
  }

  private async getNewCreatedObservationFeedbacks(): Promise<
    ObservationFeedbackRecord[]
  > {
    // MC20-18126: Push the syncStatus filter into Dexie. Previous version
    // loaded every non-Deleted feedback row into memory and then filtered
    // in JS — wasteful when most rows are already Synced.
    const data = await this.observationFeedbacks
      .where('syncStatus')
      .equals(SyncRecordStatus.CreatedButNotSynced)
      .toArray();
    data.forEach((r) => (r.syncStatus = SyncRecordStatus.Created));
    return data.map(
      ({ draftTeacherResponse, ...rest }) => rest,
    ) as ObservationFeedbackRecord[];
  }

  async getAllObservationFeedbacks(): Promise<ObservationFeedbackRecord[]> {
    return this.observationFeedbacks
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  // Data validation and recovery methods
  async getObservationAudioStatus(
    observationId: string,
  ): Promise<{ hasAudio: boolean; audioFileName?: string }> {
    // Import audioRecordingDB at runtime to avoid circular dependencies
    const { audioRecordingDB } = await import('./audio-recording');

    const audioRecording = await audioRecordingDB.audioRecordings
      .where('referenceId')
      .equals(observationId)
      .first();

    return {
      hasAudio: !!audioRecording,
      audioFileName: audioRecording?.fileName,
    };
  }

  async getOrphanedObservations(): Promise<ObservationRecord[]> {
    const { audioRecordingDB } = await import('./audio-recording');

    const allObservations = await this.observations
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.status === ObservationStatus.InProgress,
      )
      .toArray();

    const orphaned: ObservationRecord[] = [];

    for (const observation of allObservations) {
      const audioStatus = await audioRecordingDB.audioRecordings
        .where('referenceId')
        .equals(observation.id)
        .first();

      if (!audioStatus) {
        orphaned.push(observation);
      }
    }

    return orphaned;
  }

  async getFailedObservations(): Promise<ObservationRecord[]> {
    return this.observations
      .filter(
        (record) =>
          record.syncStatus === SyncRecordStatus.Failed &&
          record.status === ObservationStatus.InProgress,
      )
      .toArray();
  }

  async resetObservationStatus(observationId: string): Promise<number> {
    return this.observations.update(observationId, {
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    });
  }

  async deleteOrphanedObservation(observationId: string): Promise<void> {
    await this.observations.update(observationId, {
      syncStatus: SyncRecordStatus.Deleted,
    });
  }

  async cleanupSyncedAudioRecordings(): Promise<number> {
    const { audioRecordingDB } = await import('./audio-recording');

    // Get all observations that have audioUrl populated (synced from backend)
    const observationsWithAudioUrl = await this.observations
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.audioUrl != null &&
          record.audioUrl.trim().length > 0,
      )
      .toArray();

    let cleanedCount = 0;

    // Delete local audio recordings for observations that have backend URL
    for (const observation of observationsWithAudioUrl) {
      const localAudio = await audioRecordingDB.getRecordingByReferenceId(
        observation.id,
      );

      if (localAudio) {
        await audioRecordingDB.deleteRecordingByReferenceId(observation.id);
        cleanedCount++;
      }
    }

    return cleanedCount;
  }

  async addObservationPhoto(
    photo: Omit<IObservationPhoto, 'id'>,
  ): Promise<number> {
    return this.observationPhotos.add({
      ...photo,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    });
  }

  async deleteObservationPhoto(id: number): Promise<void> {
    await this.observationPhotos.delete(id);
  }

  async getObservationPhotos(
    observationId: string,
  ): Promise<IObservationPhoto[]> {
    return this.observationPhotos
      .where('observationId')
      .equals(observationId)
      .toArray();
  }
}

export const coachingDB = new CoachingDB();
